import type { ChangeEvent } from 'react';
import { DataGrid } from '../../components/data-grid/DataGrid';
import type { GridColumn } from '../../components/data-grid/DataGrid';
import { formatDate } from '../../components/data-grid/format';
import { formatMoney } from '../../components/data-grid/money';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import type { DocumentPeriod } from '../documents/filters';
import { journalTotals, runningBalance, toJournalRows } from './entries';
import type { JournalEntry, JournalFilters, JournalRow } from './entries';

export interface JournalViewProps {
  periods: readonly DocumentPeriod[];
  documentTypes: ReadonlyArray<{ code: string; name: string }>;
  filters: JournalFilters;
  entries: readonly JournalEntry[];
  documentLabels?: Record<string, string>;
  total?: number;
  page?: number;
  pageSize?: number;
  problem?: Problem | null;
  loading?: boolean;
  onFiltersChange(next: JournalFilters): void;
  onApply(): void;
  onReset(): void;
  onPageChange?(page: number): void;
  onOpenDocument(documentId: string): void;
}

const DIM_SLOTS = ['dim1', 'dim2', 'dim3', 'dim4', 'dim5', 'dim6', 'dim7', 'dim8'];

export function JournalView({
  periods,
  documentTypes,
  filters,
  entries,
  documentLabels = {},
  total,
  page = 1,
  pageSize = 50,
  problem = null,
  loading = false,
  onFiltersChange,
  onApply,
  onReset,
  onPageChange,
  onOpenDocument,
}: JournalViewProps): JSX.Element {
  const { t, language } = useI18n();
  const rows = toJournalRows(entries);
  const totals = journalTotals(entries);
  const balances = runningBalance(entries);
  const balanceByKey = new Map(rows.map((row, index) => [row.key, balances[index] ?? '0']));
  const totalRows = total ?? entries.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));

  const set = (patch: Partial<JournalFilters>): void => onFiltersChange({ ...filters, ...patch });

  const onDimension = (slot: string, value: string): void => {
    const dims = { ...filters.dims };
    if (value === '') {
      delete dims[slot];
    } else {
      dims[slot] = value;
    }
    onFiltersChange({ ...filters, dims });
  };

  const columns: Array<GridColumn<JournalRow>> = [
    { key: 'date', label: t('journalColumnDate'), render: (row) => formatDate(row.date) },
    {
      key: 'entry',
      label: t('journalColumnEntry'),
      render: (row) => (
        <span style={{ fontFamily: 'var(--mono)' }}>
          {row.isReversal ? `${t('journalReversal')} ` : ''}
          {row.entryId.slice(0, 12)}
        </span>
      ),
    },
    { key: 'debit', label: t('journalColumnDebit'), render: (row) => row.debitAccounts },
    { key: 'credit', label: t('journalColumnCredit'), render: (row) => row.creditAccounts },
    {
      key: 'amount',
      label: t('journalColumnAmount'),
      align: 'right',
      render: (row) => formatMoney(row.amount, language),
    },
    { key: 'dimensions', label: t('journalColumnDimensions'), render: (row) => row.dimensions },
    {
      key: 'running',
      label: t('journalRunning'),
      align: 'right',
      render: (row) => formatMoney(balanceByKey.get(row.key) ?? '0', language),
    },
    {
      key: 'source',
      label: t('journalColumnSource'),
      render: (row) => (
        <a
          href={`/documents/${row.documentId}`}
          onClick={(event) => {
            event.preventDefault();
            onOpenDocument(row.documentId);
          }}
        >
          {documentLabels[row.documentId] ?? row.documentId.slice(0, 8)}
        </a>
      ),
    },
  ];

  return (
    <section>
      <div className="page-head">
        <h1>{t('journalTitle')}</h1>
        <p className="muted">{t('journalSubtitle')}</p>
      </div>

      <div className="card" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <label className="field">
          <span>{t('journalFilterPeriod')}</span>
          <select className="input" value={filters.periodId} onChange={(event) => set({ periodId: event.target.value })}>
            <option value="">{t('journalAll')}</option>
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {t(`documentsMonth${period.month}`)} {period.year}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>{t('journalFilterAccount')}</span>
          <input
            className="input"
            name="accountPrefix"
            placeholder={t('journalFilterAccountPlaceholder')}
            value={filters.accountPrefix}
            onChange={(event) => set({ accountPrefix: event.target.value })}
          />
        </label>

        <label className="field">
          <span>{t('journalFilterFrom')}</span>
          <input
            className="input"
            type="date"
            value={filters.from}
            onChange={(event) => set({ from: event.target.value })}
          />
        </label>

        <label className="field">
          <span>{t('journalFilterTo')}</span>
          <input
            className="input"
            type="date"
            value={filters.to}
            onChange={(event) => set({ to: event.target.value })}
          />
        </label>

        <label className="field">
          <span>{t('journalFilterDocumentType')}</span>
          <select
            className="input"
            value={filters.documentType}
            onChange={(event) => set({ documentType: event.target.value })}
          >
            <option value="">{t('journalAll')}</option>
            {documentTypes.map((type) => (
              <option key={type.code} value={type.code}>
                {type.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>{t('journalFilterSide')}</span>
          <select
            className="input"
            value={filters.side}
            onChange={(event) => set({ side: event.target.value as JournalFilters['side'] })}
          >
            <option value="">{t('journalAll')}</option>
            <option value="D">{t('journalSideDebit')}</option>
            <option value="C">{t('journalSideCredit')}</option>
          </select>
        </label>

        {DIM_SLOTS.map((slot) => (
          <label className="field" key={slot}>
            <span>
              {t('journalColumnDimensions')} {slot}
            </span>
            <input
              className="input"
              name={slot}
              value={filters.dims[slot] ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onDimension(slot, event.target.value)}
            />
          </label>
        ))}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button type="button" className="btn btn--primary" onClick={onApply}>
            {t('journalFilterApply')}
          </button>
          <button type="button" className="btn" onClick={onReset}>
            {t('journalFilterReset')}
          </button>
        </div>
      </div>

      {problem !== null ? (
        <div className="alert alert--danger" role="alert" data-testid="journal-problem" style={{ marginTop: 12 }}>
          <strong>{t('journalProblemTitle')}</strong> <code>{problem.code}</code> — {problem.detail}
        </div>
      ) : null}

      <div
        className="card"
        style={{ marginTop: 16, display: 'flex', gap: 24, alignItems: 'center', justifyContent: 'flex-end' }}
        data-testid="journal-totals"
      >
        <span>
          {t('journalTotalDebit')}{' '}
          <strong data-testid="journal-total-debit">{formatMoney(totals.debit, language)}</strong>
        </span>
        <span>
          {t('journalTotalCredit')}{' '}
          <strong data-testid="journal-total-credit">{formatMoney(totals.credit, language)}</strong>
        </span>
        <span>
          {t('journalDifference')}{' '}
          <strong data-testid="journal-difference">{formatMoney(totals.difference, language)}</strong>
        </span>
        <span
          className="badge"
          data-testid="journal-balanced"
          data-balanced={totals.balanced ? 'true' : 'false'}
          style={{ color: totals.balanced ? '#1f7a4d' : '#a33' }}
        >
          {totals.balanced ? t('journalBalanced') : t('journalUnbalanced')}
        </span>
      </div>

      <div style={{ marginTop: 16 }}>
        {loading ? (
          <p className="muted">{t('journalLoading')}</p>
        ) : (
          <DataGrid
            testId="journal-grid"
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.key}
            rowHeight={40}
            viewportHeight={480}
            emptyText={t('journalEmpty')}
            onRowClick={(row) => onOpenDocument(row.documentId)}
          />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <span className="muted">
          {rows.length} / {totalRows} {t('journalRows')}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
          >
            {t('journalPrev')}
          </button>
          <span className="muted">
            {page} / {pageCount} {t('journalPage')}
          </span>
          <button
            type="button"
            className="btn"
            disabled={page >= pageCount}
            onClick={() => onPageChange?.(page + 1)}
          >
            {t('journalNext')}
          </button>
        </span>
      </div>
    </section>
  );
}
